import {DI_TOKENS} from '@/di/di.tokens';
import {IUserStatistic} from '@/stores/ItemsStore/ItemsStore.types';

import {injected} from 'brandi';

import {
  NamedResourcesStatisticItem,
  NamedStatisticContent,
  NamedStatisticDefault,
  NamedValidationConfig,
  ResourcesAll,
} from '@/resources';
import {FrontendError} from '@/services/ErrorService/FrontendError';
import {LoggerService} from '@/services/LoggerService/LoggerService';
import {StatisticMapper, TStatisticMapperOutput} from '@/utils/StatisticMapper/StatisticMapper';
import {ExtractedData, TransformUtils} from '@/utils/TransformUtils';

type TMappedStatisticResources = {
  mappedStatisticItems: Record<string, ExtractedData<NamedResourcesStatisticItem>>;
  mappedStatisticContent: Record<string, ExtractedData<NamedStatisticContent>>;
  mappedStatisticValidation: Record<string, ExtractedData<NamedValidationConfig>>;
  mappedStatisticDefault: Record<string, ExtractedData<NamedStatisticDefault>>;
};

export class ItemContentSharedUseCase {
  constructor(
    private readonly resources: ResourcesAll,
    private readonly loggerService: LoggerService,
  ) {}

  public getItemContent = (userStatistic: IUserStatistic[]): TStatisticMapperOutput[] => {
    const statistic = this.getSortedItemStatistic(userStatistic);
    const {mappedStatisticValidation, mappedStatisticContent} = this.getMappedStatisticResources();

    // маппим статистику, для которой установлен конфиг контента
    return statistic
      .filter((singleItemStatistic) => {
        if (mappedStatisticContent[singleItemStatistic.metricDesc]) {
          return true;
        } else {
          // логируем отсутствие конфига контента для статистики
          const error = FrontendError.createFindStatisticContentConfigError(singleItemStatistic.metricDesc);
          this.loggerService.logParseStatisticError(error);

          return false;
        }
      })
      .map((singleItemStatistic) => {
        return StatisticMapper.map({
          singleItemStatistic,
          singleItemStatisticContent: mappedStatisticContent[singleItemStatistic.metricDesc],
          singleItemStatisticValidation: mappedStatisticValidation[singleItemStatistic.metricDesc],
          logError: (error) => this.loggerService.logParseStatisticError(error),
          shouldLogError: false,
        });
      })
      .filter(TransformUtils.notEmpty);
  };

  public getMappedStatisticResources = (): TMappedStatisticResources => {
    const {statisticItems, statisticContent, statisticValidationConfig, statisticDefault} = this.resources;
    const mappedStatisticValidation = TransformUtils.transformNamedItemsArrayToObject(statisticValidationConfig);
    const mappedStatisticContent = TransformUtils.transformNamedItemsArrayToObject(statisticContent);
    const mappedStatisticItems = TransformUtils.transformNamedItemsArrayToObject(statisticItems);
    const mappedStatisticDefault = TransformUtils.transformNamedItemsArrayToObject(statisticDefault);

    return {
      mappedStatisticItems,
      mappedStatisticContent,
      mappedStatisticValidation,
      mappedStatisticDefault,
    };
  };

  private getSortedItemStatistic = (statistic: IUserStatistic[]): IUserStatistic[] => {
    return [...statistic].sort((a, b) => b.weight - a.weight);
  };
}

injected(ItemContentSharedUseCase, DI_TOKENS.resources, DI_TOKENS.loggerService);
